/**
 * Icon utility functions
 */
import { ICON_TYPES } from './types'
import { MATERIAL_SYMBOLS } from './material-symbols'

/**
 * Parse an icon string into its components
 * @param {string} iconStr - Icon string like "material:home" or "lucide:sword" or "image:https://..."
 * @returns {{ type: string, value: string } | null}
 */
export function parseIconString(iconStr) {
  if (!iconStr) return null

  // Handle object format (already parsed)
  if (typeof iconStr === 'object' && iconStr.type && iconStr.value) {
    return iconStr
  }

  if (typeof iconStr !== 'string') return null

  const str = iconStr.trim()

  // Check for type:value format
  if (str.includes(':')) {
    const colonIdx = str.indexOf(':')
    const type = str.slice(0, colonIdx).toLowerCase()
    const value = str.slice(colonIdx + 1)

    if (type === 'material' || type === 'mat' || type === 'mi') {
      return { type: ICON_TYPES.MATERIAL, value }
    }
    if (type === 'material-symbols' || type === 'ms' || type === 'symbols') {
      return { type: ICON_TYPES.MATERIAL_SYMBOLS, value }
    }
    if (type === 'lucide' || type === 'lu') {
      return { type: ICON_TYPES.LUCIDE, value }
    }
    if (type === 'fa' || type === 'fontawesome') {
      return { type: ICON_TYPES.FONTAWESOME, value }
    }
    if (type === 'img' || type === 'image' || type === 'url') {
      return { type: ICON_TYPES.IMAGE, value }
    }
    if (type === 'emoji' || type === 'em') {
      return { type: ICON_TYPES.EMOJI, value }
    }
    if (type === 'symbol' || type === 'sym' || type === 'code') {
      return { type: ICON_TYPES.SYMBOL, value }
    }
    // If starts with http, treat as image
    if (str.startsWith('http://') || str.startsWith('https://')) {
      return { type: ICON_TYPES.IMAGE, value: str }
    }
  }

  // Default: treat bare strings as Material Symbols if they exist there, else Material Icons
  if (MATERIAL_SYMBOLS.includes(str)) {
    return { type: ICON_TYPES.MATERIAL_SYMBOLS, value: str }
  }

  // Default: assume Material icon
  return { type: ICON_TYPES.MATERIAL, value: str }
}

/**
 * Convert icon object to string format for storage
 */
export function iconToString(icon) {
  if (!icon) return ''
  if (typeof icon === 'string') return icon
  if (!icon.type || !icon.value) return ''
  return `${icon.type}:${icon.value}`
}

/**
 * Get CSS classes or HTML for rendering an icon
 */
export function getIconHTML(icon, options = {}) {
  const parsed = parseIconString(icon)
  if (!parsed) return ''

  const { size = '24px', className = '' } = options

  switch (parsed.type) {
    case ICON_TYPES.MATERIAL:
      return `<i class="material-icons ${className}" style="font-size: ${size}">${parsed.value}</i>`

    case ICON_TYPES.MATERIAL_SYMBOLS:
      return `<i class="material-symbols-outlined ${className}" style="font-size: ${size}">${parsed.value}</i>`

    case ICON_TYPES.LUCIDE:
      // Lucide icons need special handling - we'll use a placeholder that gets replaced
      return `<span class="kb-icon kb-icon-lucide ${className}" data-icon="${parsed.value}" style="width: ${size}; height: ${size}"></span>`

    case ICON_TYPES.FONTAWESOME: {
      const faClass = parsed.value.startsWith('fa-') ? parsed.value : `fa-${parsed.value}`
      return `<i class="fa-solid ${faClass} ${className}" style="font-size: ${size}"></i>`
    }

    case ICON_TYPES.IMAGE:
      // For inline images, set width only; let height auto-scale to preserve aspect ratio
      return `<img src="${parsed.value}" class="kb-icon kb-icon-image ${className}" style="width: ${size}; object-fit: contain" alt="icon" />`

    case ICON_TYPES.EMOJI:
      return `<span class="kb-icon kb-icon-emoji ${className}" style="font-size: ${size}; line-height: 1">${parsed.value}</span>`

    case ICON_TYPES.SYMBOL:
      return `<span class="kb-icon kb-icon-symbol ${className}" style="font-size: calc(${size} * 0.7); font-family: monospace; font-weight: bold; line-height: 1">${parsed.value}</span>`

    default:
      return `<i class="material-icons ${className}" style="font-size: ${size}">${parsed.value}</i>`
  }
}

export default {
  parseIconString,
  iconToString,
  getIconHTML
}
